import React, { useState, useEffect } from 'react';
import { Card, Accordion, Table, Badge, Alert, Row, Col, ProgressBar, Tabs, Tab } from 'react-bootstrap';
import { useUserProgress } from '@hooks';

/**
 * User Progress View Component
 * Displays detailed progress for a user in a course
 */
export function UserProgressView({ user, courseId, courseName }) {
  const {
    progress,
    moduleProgress,
    questionProgress,
    loading,
    error,
    statistics,
    getModuleQuestions,
  } = useUserProgress(user?.id, courseId);

  /**
   * Format date
   */
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  /**
   * Get mastery level color
   */
  const getMasteryColor = (level) => {
    if (level >= 0.8) return 'success';
    if (level >= 0.5) return 'warning';
    return 'danger';
  };

  /**
   * Get question type label
   */
  const getQuestionTypeLabel = (type) => {
    const labels = {
      multiple_choice: 'Multiple Choice',
      true_false: 'True/False',
      fill_blank: 'Fill in Blank',
      matching: 'Matching',
      drag_drop: 'Drag & Drop',
      multiple_select: 'Multiple Select',
    };
    return labels[type] || type;
  };

  if (loading) {
    return <div className="text-center py-4">Loading progress...</div>;
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (!progress) {
    return (
      <Alert variant="info">
        {user?.name} has not started this course yet.
      </Alert>
    );
  }

  return (
    <div>
      {/* Overall Statistics */}
      <Card className="mb-3">
        <Card.Header>
          <h5 className="mb-0">
            {user?.name}'s Progress in {courseName}
          </h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={3}>
              <div className="text-center">
                <h4>{Math.round(statistics.overallScore * 100)}%</h4>
                <small className="text-muted">Overall Score</small>
              </div>
            </Col>
            <Col md={3}>
              <div className="text-center">
                <h4>{statistics.completedModules} / {statistics.totalModules}</h4>
                <small className="text-muted">Modules Completed</small>
              </div>
            </Col>
            <Col md={3}>
              <div className="text-center">
                <h4>{statistics.totalAttempts}</h4>
                <small className="text-muted">Total Attempts</small>
              </div>
            </Col>
            <Col md={3}>
              <div className="text-center">
                <h4>
                  {statistics.totalAttempts > 0
                    ? Math.round((statistics.correctAttempts / statistics.totalAttempts) * 100)
                    : 0}%
                </h4>
                <small className="text-muted">Success Rate</small>
              </div>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col>
              <small className="text-muted">
                Last Activity: {formatDate(progress.lastActivityAt)}
              </small>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Detailed Progress Tabs */}
      <Tabs defaultActiveKey="modules" className="mb-3">
        {/* Module Progress Tab */}
        <Tab eventKey="modules" title="Module Progress">
          <Card>
            <Card.Body>
              {moduleProgress.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  No module progress data available.
                </div>
              ) : (
                <Accordion>
                  {moduleProgress.map((module, index) => (
                    <Accordion.Item eventKey={module.moduleId} key={module.moduleId}>
                      <Accordion.Header>
                        <div className="d-flex justify-content-between align-items-center w-100 me-3">
                          <span>
                            {module.moduleName}
                            {module.isCompleted && (
                              <Badge bg="success" className="ms-2">Completed</Badge>
                            )}
                            {!module.isUnlocked && (
                              <Badge bg="secondary" className="ms-2">Locked</Badge>
                            )}
                          </span>
                          <span className="text-muted">
                            Score: {Math.round(module.score * 100)}%
                          </span>
                        </div>
                      </Accordion.Header>
                      <Accordion.Body>
                        <Row className="mb-3">
                          <Col md={6}>
                            <strong>Questions:</strong> {module.completedQuestions} / {module.totalQuestions}
                          </Col>
                          <Col md={6}>
                            <strong>Last Activity:</strong> {formatDate(module.lastActivity)}
                          </Col>
                        </Row>
                        <ProgressBar 
                          now={(module.completedQuestions / module.totalQuestions) * 100} 
                          label={`${Math.round((module.completedQuestions / module.totalQuestions) * 100)}%`}
                        />
                        
                        {/* Questions in this module */}
                        <div className="mt-3">
                          <h6>Questions in this Module:</h6>
                          {getModuleQuestions(module.moduleId).length === 0 ? (
                            <p className="text-muted">No questions attempted yet.</p>
                          ) : (
                            <Table size="sm" striped>
                              <thead>
                                <tr>
                                  <th>Question</th>
                                  <th>Type</th>
                                  <th>Attempts</th>
                                  <th>Correct</th>
                                  <th>Mastery</th>
                                </tr>
                              </thead>
                              <tbody>
                                {getModuleQuestions(module.moduleId).map(q => (
                                  <tr key={q.questionId}>
                                    <td>{q.questionText.substring(0, 50)}...</td>
                                    <td>
                                      <Badge bg="info" pill>
                                        {getQuestionTypeLabel(q.questionType)}
                                      </Badge>
                                    </td>
                                    <td>{q.attempts}</td>
                                    <td>{q.correctAttempts}</td>
                                    <td>
                                      <Badge bg={getMasteryColor(q.masteryLevel)}>
                                        {Math.round(q.masteryLevel * 100)}%
                                      </Badge>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          )}
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  ))}
                </Accordion>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* All Questions Tab */}
        <Tab eventKey="questions" title="All Questions">
          <Card>
            <Card.Body>
              {questionProgress.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  No questions attempted yet.
                </div>
              ) : (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Module</th>
                      <th>Question</th>
                      <th>Type</th>
                      <th>Attempts</th>
                      <th>Correct Attempts</th>
                      <th>Success Rate</th>
                      <th>Mastery Level</th>
                      <th>Last Attempt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {questionProgress.map(q => (
                      <tr key={q.questionId}>
                        <td>{q.moduleName}</td>
                        <td>{q.questionText.substring(0, 60)}...</td>
                        <td>
                          <Badge bg="info" pill>
                            {getQuestionTypeLabel(q.questionType)}
                          </Badge>
                        </td>
                        <td>{q.attempts}</td>
                        <td>{q.correctAttempts}</td>
                        <td>
                          {q.attempts > 0
                            ? `${Math.round((q.correctAttempts / q.attempts) * 100)}%`
                            : 'N/A'}
                        </td>
                        <td>
                          <Badge bg={getMasteryColor(q.masteryLevel)}>
                            {Math.round(q.masteryLevel * 100)}%
                          </Badge>
                        </td>
                        <td>{formatDate(q.lastAttemptAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>
    </div>
  );
}
